import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Wallet, Plus, Copy, ExternalLink, Trash2, 
  Search, Building2, User, Briefcase, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Known institutional addresses (public info)
const KNOWN_ADDRESSES = [
  { 
    name: 'BETH Token Contract', 
    address: '0x6822824c5349bf005e353ec5dda3fa34595ea811',
    category: 'contract',
    verified: true
  },
  { 
    name: 'Bethereum Treasury', 
    address: '0x2FB355B99b2060e42DB50D99393D00f47A41d322',
    category: 'treasury',
    verified: true
  },
  { 
    name: 'Collateral Vault', 
    address: '0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d',
    category: 'contract',
    verified: true
  },
  { 
    name: 'Platform Reserve', 
    address: '0x8F01D20feaA323049976f35865F43D3C457F19CF',
    category: 'contract',
    verified: true
  }
];

const CATEGORIES = [
  { id: 'all', label: 'All', icon: Wallet },
  { id: 'personal', label: 'My Addresses', icon: User },
  { id: 'exchange', label: 'Exchanges', icon: Building2 },
  { id: 'business', label: 'Business', icon: Briefcase },
  { id: 'favorite', label: 'Favorites', icon: Star }
];

const MyAddressBook = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [addresses, setAddresses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    name: '',
    address: '',
    category: 'personal',
    notes: ''
  });

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = () => {
    const saved = localStorage.getItem('myAddressBook');
    if (saved) {
      setAddresses(JSON.parse(saved));
    }
  };

  const saveAddresses = (updated) => {
    localStorage.setItem('myAddressBook', JSON.stringify(updated));
    setAddresses(updated);
  };

  const validateAddress = (address) => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  const handleAddAddress = () => {
    if (!newAddress.name || !newAddress.address) {
      toast.error('Please enter both name and address');
      return;
    }

    if (!validateAddress(newAddress.address)) {
      toast.error('Invalid Ethereum address');
      return;
    }

    // Check for duplicates
    if (addresses.some(a => a.address.toLowerCase() === newAddress.address.toLowerCase())) {
      toast.error('Address already exists');
      return;
    }

    const addressRecord = {
      ...newAddress,
      address: newAddress.address.toLowerCase(),
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };

    const updated = [addressRecord, ...addresses];
    saveAddresses(updated);
    setNewAddress({ name: '', address: '', category: 'personal', notes: '' });
    setShowAddForm(false);
    toast.success('Address saved!');
  };

  const handleDeleteAddress = (id) => {
    const updated = addresses.filter(a => a.id !== id);
    saveAddresses(updated);
    toast.success('Address removed');
  };

  const toggleFavorite = (id) => {
    const updated = addresses.map(a => 
      a.id === id ? { ...a, category: a.category === 'favorite' ? 'personal' : 'favorite' } : a
    );
    saveAddresses(updated);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Address copied!');
  };

  const getFilteredAddresses = () => {
    let filtered = addresses;
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(a => a.category === categoryFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const getCategoryColor = (category) => {
    const colors = {
      personal: 'text-blue-400 bg-blue-900/30',
      exchange: 'text-purple-400 bg-purple-900/30',
      business: 'text-green-400 bg-green-900/30',
      favorite: 'text-yellow-400 bg-yellow-900/30',
      contract: 'text-gray-400 bg-gray-900/30',
      treasury: 'text-[#b8860b] bg-[#b8860b]/20',
      pool: 'text-pink-400 bg-pink-900/30'
    };
    return colors[category] || colors.personal;
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="my-address-book">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#b8860b]" />
                Address Book
              </h1>
              <p className="text-xs text-gray-500">Save and manage wallet addresses</p>
            </div>
          </div>
          <Button 
            className="bg-[#b8860b] hover:bg-[#9a7209]"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Address
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search by name or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#0d1d35] border-[#1a2d4a] text-white"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(cat => (
              <Button
                key={cat.id}
                variant={categoryFilter === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter(cat.id)}
                className={categoryFilter === cat.id ? 'bg-[#b8860b]' : 'border-[#1a2d4a]'}
              >
                <cat.icon className="w-4 h-4 mr-1" />
                {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white">Add New Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Name *</label>
                  <Input
                    placeholder="e.g., My Coinbase"
                    value={newAddress.name}
                    onChange={(e) => setNewAddress({...newAddress, name: e.target.value})}
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Category</label>
                  <select
                    value={newAddress.category}
                    onChange={(e) => setNewAddress({...newAddress, category: e.target.value})}
                    className="w-full p-2 rounded bg-[#1a2d4a] border border-[#2a3d5a] text-white"
                  >
                    <option value="personal">My Addresses</option>
                    <option value="exchange">Exchanges</option>
                    <option value="business">Business</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Wallet Address *</label>
                <Input
                  placeholder="0x..."
                  value={newAddress.address}
                  onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                  className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Notes (optional)</label>
                <Input
                  placeholder="Any notes about this address..."
                  value={newAddress.notes}
                  onChange={(e) => setNewAddress({...newAddress, notes: e.target.value})}
                  className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAddAddress} className="bg-[#b8860b] hover:bg-[#9a7209]">
                  Save Address
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)} className="border-[#1a2d4a]">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Addresses */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#b8860b]" />
              Platform Addresses
            </CardTitle>
            <CardDescription>Official Bethereum contract addresses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {KNOWN_ADDRESSES.map((addr, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getCategoryColor(addr.category)}`}>
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{addr.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{addr.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(addr.address)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <a 
                      href={`https://etherscan.io/address/${addr.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* My Addresses */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              My Saved Addresses
            </CardTitle>
            <CardDescription>Your personal address book</CardDescription>
          </CardHeader>
          <CardContent>
            {getFilteredAddresses().length === 0 ? (
              <div className="text-center py-8">
                <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No addresses saved yet</p>
                <Button 
                  className="mt-4 bg-[#b8860b] hover:bg-[#9a7209]"
                  onClick={() => setShowAddForm(true)}
                >
                  Add Your First Address
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {getFilteredAddresses().map((addr) => (
                  <div 
                    key={addr.id}
                    className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg hover:bg-[#1a2d4a] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getCategoryColor(addr.category)}`}>
                        {addr.category === 'favorite' ? <Star className="w-4 h-4" /> : <User className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{addr.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{addr.address}</p>
                        {addr.notes && (
                          <p className="text-xs text-gray-600 mt-1">{addr.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleFavorite(addr.id)}
                        className={addr.category === 'favorite' ? 'text-yellow-400' : 'text-gray-400'}
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(addr.address)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <a 
                        href={`https://etherscan.io/address/${addr.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteAddress(addr.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MyAddressBook;
